import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {CommonUtils} from '../utils/common';
import {Observable} from 'rxjs';
import {VetineoApiResponse} from '../models/vetineo-api-response.model';
import {map, take} from 'rxjs/operators';
import {VetClinicPetSpecies} from "../models/vet-clinic-pet-species.model";
import {VetClinicPetSpeciesDtoList} from "../models/vet-clinic-pet-species-dto-list.model";
import {PetSpeciesGroup} from "../models/pet-species-group.model";

/**
 * Serwis warstwy danych zarządzający słownikami gatunków zwierząt.
 * Obsługuje mapowanie pomiędzy globalnym słownikiem
 * a indywidualnymi ustawieniami placówki weterynaryjnej,
 * definiując zakres usług świadczonych przez daną klinikę.
 */
@Injectable({
  providedIn: 'root'
})
export class VetClinicPetSpeciesService {

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) {
  }

  /**
   * Pobiera lokalną listę gatunków zwierząt, które dana klinika zadeklarowała jako obsługiwane.
   *
   * @param vetcodeId Unikalny identyfikator/kod kliniki weterynaryjnej.
   * @returns Observable emitujący przefiltrowaną tablicę obsługiwanych gatunków.
   */
  getPetSpecies(vetcodeId: string): Observable<VetClinicPetSpecies[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinic-pet-species/${vetcodeId}`,).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinicPetSpecies(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera globalny słownik grup gatunków zwierząt.
   * Wykorzystywany do grupowania gatunków zwierząt podczas wybierania gatunków obsługiwanych przez lecznice.
   * @returns Observable emitujący pełną tablicę dostępnych grup gatunków.
   */
  getGlobalSpeciesGroups(): Observable<PetSpeciesGroup[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/pet-species/groups').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new PetSpeciesGroup(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera globalny słownik wszystkich możliwych gatunków zwierząt.
   * Służy do zasilania formularzy wyboru (np. checkboxów w panelu konfiguracji placówki).
   *
   * @returns Observable emitujący pełną tablicę dostępnych gatunków.
   */
  getGlobalSpecies(): Observable<VetClinicPetSpecies[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/pet-species').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinicPetSpecies(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje nową konfigurację obsługiwanych gatunków dla kliniki.
   * Wykorzystuje wzorzec DTO do masowej aktualizacji powiązań
   * pomiędzy kliniką a elementami globalnego słownika.
   *
   * @param vetClinicPetSpeciesDto Obiekt transferowy (DTO) zawierający listę wybranych gatunków przez lecznicę.
   * @returns Observable emitujący zaktualizowaną tablicę obsługiwanych gatunków przypisanych do placówki.
   */
  sendPetSpecies(vetClinicPetSpeciesDto: VetClinicPetSpeciesDtoList): Observable<VetClinicPetSpecies[]> {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/vet-clinic-pet-species`, vetClinicPetSpeciesDto).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinicPetSpecies(), [], vetineoApiResponse.response);
      } else {
        if (vetineoApiResponse.errors) {
          return undefined;
        }
      }
    }));
  }
}
